import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, Plus, Minus } from 'lucide-react';

export const WalletManager = ({ wallet, onTopup, onDeduct, onUpdateSettings, walletTxns }) => {
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [deductAmount, setDeductAmount] = useState('');
  const [deductNote, setDeductNote] = useState('');

  const handleTopup = () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) return;
    onTopup(amt, topupNote);
    setTopupAmount('');
    setTopupNote('');
  };

  const handleDeduct = () => {
    const amt = parseFloat(deductAmount);
    if (!amt || amt <= 0) return;
    onDeduct(amt, deductNote);
    setDeductAmount('');
    setDeductNote('');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="risk-wallet-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
          <Wallet className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Gestione Credito TelcaVoIP</h2>
          <p className="text-xs text-slate-500">Aggiungi, sottrai credito, impostazioni e transazioni</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Top-up */}
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
          <div className="flex items-center gap-2 mb-3">
            <Plus className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-emerald-700">Aggiungi Credito</h3>
          </div>
          <div className="space-y-2">
            <Input type="number" step="0.01" min="0" value={topupAmount} onChange={e => setTopupAmount(e.target.value)} placeholder="Importo (€)" className="text-sm" data-testid="wallet-topup-amount" />
            <Input value={topupNote} onChange={e => setTopupNote(e.target.value)} placeholder="Nota (opzionale)" className="text-sm" data-testid="wallet-topup-note" />
            <Button onClick={handleTopup} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm" data-testid="wallet-topup-btn">
              <Plus className="w-3 h-3 mr-1" /> Ricarica
            </Button>
          </div>
        </div>

        {/* Deduct */}
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <div className="flex items-center gap-2 mb-3">
            <Minus className="w-4 h-4 text-red-600" />
            <h3 className="text-sm font-semibold text-red-700">Sottrai Credito</h3>
          </div>
          <div className="space-y-2">
            <Input type="number" step="0.01" min="0" value={deductAmount} onChange={e => setDeductAmount(e.target.value)} placeholder="Importo (€)" className="text-sm" data-testid="wallet-deduct-amount" />
            <Input value={deductNote} onChange={e => setDeductNote(e.target.value)} placeholder="Motivo sottrazione" className="text-sm" data-testid="wallet-deduct-note" />
            <Button onClick={handleDeduct} className="w-full bg-red-600 hover:bg-red-700 text-white text-sm" data-testid="wallet-deduct-btn">
              <Minus className="w-3 h-3 mr-1" /> Sottrai
            </Button>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-violet-50 rounded-xl p-4 border border-violet-200">
          <h3 className="text-sm font-semibold text-violet-700 mb-3">Impostazioni</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-violet-600">Maggiorazione (%)</Label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min="0" max="500" defaultValue={wallet.markup_pct} id="markup-input" className="text-sm" data-testid="wallet-markup-input" />
                <Button size="sm" variant="outline" onClick={() => onUpdateSettings('markup_pct', document.getElementById('markup-input').value)} data-testid="wallet-markup-save">Salva</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-violet-600">Soglia Alert (€)</Label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min="0" step="0.01" defaultValue={wallet.alert_threshold} id="threshold-input" className="text-sm" data-testid="wallet-threshold-input" />
                <Button size="sm" variant="outline" onClick={() => onUpdateSettings('alert_threshold', document.getElementById('threshold-input').value)} data-testid="wallet-threshold-save">Salva</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Table with markup */}
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Listino TelcaVoIP (+{wallet.markup_pct}%)</h3>
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
            {wallet.cost_table && Object.entries(wallet.cost_table).map(([key, val]) => (
              <div key={key} className="flex items-center justify-between text-xs">
                <span className="text-slate-600 truncate mr-2">{val.label}</span>
                <div className="flex gap-2 flex-shrink-0">
                  <span className="text-slate-400 line-through">{val.base_cost.toFixed(2)}€</span>
                  <span className="font-bold text-teal-700">{val.telcavoip_cost.toFixed(2)}€</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">Barrato = costo base. Grassetto = prezzo TelcaVoIP.</p>
        </div>
      </div>

      {/* Transaction History */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Ultime Transazioni</h3>
        <div className="max-h-[250px] overflow-y-auto space-y-1">
          {walletTxns.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nessuna transazione</p>
          ) : walletTxns.slice(0, 50).map((tx, i) => (
            <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg text-xs">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${tx.type === 'topup' ? 'bg-emerald-500' : 'bg-red-400'}`} />
              <span className={`font-bold min-w-[70px] ${tx.type === 'topup' ? 'text-emerald-600' : 'text-red-500'}`}>
                {tx.type === 'topup' ? '+' : ''}{tx.amount.toFixed(2)}€
              </span>
              <span className="text-slate-500 flex-1 truncate">
                {tx.type === 'topup' ? (tx.note || 'Ricarica') : tx.type === 'deduct' ? (tx.note || 'Sottrazione manuale') : tx.report_label || tx.report_type}
              </span>
              <span className="text-slate-400 flex-shrink-0">{tx.balance_after?.toFixed(2)}€</span>
              <span className="text-slate-300 flex-shrink-0">{new Date(tx.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
